import type { ReactNode } from "react";
import { useState, useRef, useEffect } from "react";
import styled from "styled-components";

/**
 * [共通パーツ]Tooltip
 */

type TooltipProps = {
  content: string | undefined;
  children: ReactNode;
  disabled?: boolean;
};

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

const TooltipContent = styled.div<{
  $visible: boolean;
  $position: { top: number; left: number };
}>`
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
  opacity: ${(props) => (props.$visible ? "1" : "0")};
  position: fixed;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.9);
  color: #fff;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  max-width: 400px;
  word-wrap: break-word;
  user-select: text;
  cursor: text;
  transition: opacity 0.2s;
  pointer-events: ${(props) => (props.$visible ? "auto" : "none")};
  top: ${(props) => props.$position.top}px;
  left: ${(props) => props.$position.left}px;
  transform: translateY(-100%) translateY(-4px);
  &::before {
    content: "";
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    height: 8px;
  }
`;

const TooltipTrigger = styled.div`
  display: inline-block;
  width: 100%;
`;

const SHOW_DELAY = 500;
const HIDE_DELAY = 200;
const SCREEN_PADDING = 8;

export function Tooltip({ content, children, disabled = false }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearShowTimeout = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
  };

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const updatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    const top = triggerRect.top;

    // 画面からはみ出さないように調整
    if (left < SCREEN_PADDING) {
      left = SCREEN_PADDING;
    } else if (left + tooltipRect.width > window.innerWidth - SCREEN_PADDING) {
      left = window.innerWidth - tooltipRect.width - SCREEN_PADDING;
    }

    setPosition({ top, left });
  };

  const handleMouseEnter = () => {
    if (disabled || !content) return;

    clearHideTimeout();

    showTimeoutRef.current = setTimeout(() => {
      setVisible(true);
      requestAnimationFrame(updatePosition);
    }, SHOW_DELAY);
  };

  const handleMouseLeave = () => {
    clearShowTimeout();

    hideTimeoutRef.current = setTimeout(() => {
      setVisible(false);
    }, HIDE_DELAY);
  };

  const handleTooltipMouseEnter = () => {
    clearHideTimeout();
    setVisible(true);
  };

  useEffect(() => {
    return () => {
      clearShowTimeout();
      clearHideTimeout();
    };
  }, []);

  useEffect(() => {
    if (visible) {
      updatePosition();
      window.addEventListener("scroll", updatePosition);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [visible]);

  return (
    <TooltipWrapper>
      <TooltipTrigger
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent
        ref={tooltipRef}
        $visible={visible}
        $position={position}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {content}
      </TooltipContent>
    </TooltipWrapper>
  );
}
